/**
 * Authenticated fetch wrapper for the Flask backend.
 *
 * Security rules (do not change):
 * - Access tokens come from sessionStore (issued by Flask /auth/*).
 * - On 401: try one refresh via Flask, then clear session and notify listeners.
 * - Test mode sends X-Tuned-Test-Mode: 1 and NO Authorization header.
 * - Secret API keys never live in the frontend bundle.
 */
import {
  getRefreshToken,
  getSession,
  setSession,
  clearSession,
  isAccessTokenExpired,
} from '../auth/sessionStore';

/**
 * Resolve Flask API origin.
 * - REACT_APP_API_BASE set (e.g. `/api` in prod behind nginx) → use it
 * - else `npm start -- --mobile` → `http://<page-hostname>:5000`
 * - else local default → `http://127.0.0.1:5000`
 */
function resolveApiBase() {
  const fromEnv = (process.env.REACT_APP_API_BASE || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (process.env.REACT_APP_MOBILE_DEBUG === '1' && typeof window !== 'undefined') {
    return `http://${window.location.hostname}:5000`;
  }
  return 'http://127.0.0.1:5000';
}

export const API_BASE = resolveApiBase();

const unauthorizedListeners = new Set();
let refreshInFlight = null;

/** Subscribe to 401 events (used by AuthProvider to prompt re-login). */
export function onUnauthorized(listener) {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

async function refreshAccessToken() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) {
        clearSession();
        return null;
      }
      const data = await res.json();
      if (!data?.access_token) {
        clearSession();
        return null;
      }
      setSession(data);
      return data.access_token;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Return a usable access token, refreshing when missing, expired, or forced.
 * Clears the cached session when refresh is impossible/fails so UI cannot
 * stay in a ghost signed-in state.
 */
export async function ensureValidAccessToken({ forceRefresh = false } = {}) {
  const session = getSession();
  if (!session) return null;

  const needsRefresh = forceRefresh
    || !session.access_token
    || isAccessTokenExpired()
    || !session.user;

  if (!needsRefresh) return session.access_token;

  if (!session.refresh_token) {
    clearSession();
    return null;
  }

  return refreshAccessToken();
}

/**
 * apiFetch('/profiles', { method, body, testMode })
 * body objects are JSON-encoded automatically. Returns the raw Response.
 */
export async function apiFetch(path, { method = 'GET', body, testMode = false, headers = {}, signal } = {}) {
  const finalHeaders = { ...headers };

  if (testMode) {
    finalHeaders['X-Tuned-Test-Mode'] = '1';
  } else {
    const token = await ensureValidAccessToken();
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  const options = { method, headers: finalHeaders };
  if (signal) options.signal = signal;
  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  let res = await fetch(`${API_BASE}${path}`, options);

  if (res.status === 401 && !testMode && getRefreshToken()) {
    const next = await refreshAccessToken();
    if (next) {
      finalHeaders['Authorization'] = `Bearer ${next}`;
      res = await fetch(`${API_BASE}${path}`, options);
    }
  }

  if (res.status === 401 && !testMode) {
    clearSession();
    unauthorizedListeners.forEach((fn) => fn());
  }

  return res;
}
